/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Grid, Hidden, Typography, Typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { ExternalLinkIcon } from '@coursera/cds-icons';

import type { UseCurriculum_AcademicEventFragment as AcademicEvent } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import bannerBackgroundImage from 'images/degree-home/banner-background.svg';
import calendarImage from 'images/degree-home/calendar.png';

import _t from 'i18n!nls/degree-home';

const styles = {
  contentContainer: (theme: Theme) => css`
    background-image: url(${bannerBackgroundImage});
    background-position: right;
    background-repeat: no-repeat;
    background-size: contain;
    padding: ${theme.spacing(32)};
    margin-top: ${theme.spacing(32)};
    margin-bottom: ${theme.spacing(32)};
    border-radius: ${theme.spacing(16)};

    ${theme.breakpoints.down('xs')} {
      background-size: cover;
      padding: ${theme.spacing(24)};
    }
  `,
  bannerHeading: (theme: Theme) => css`
    margin-bottom: ${theme.spacing(8)};
    font-weight: 400;
    line-height: 24px;
    font-size: 28px;
    /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
    font-weight: 600;
    /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
    line-height: 36px;
  `,
  bannerDescription: (theme: Theme) => css`
    margin-bottom: ${theme.spacing(24)};
  `,
  enrollBtn: (theme: Theme) => css`
    margin-top: ${theme.spacing(24)};
  `,
  bottomSpace: css`
    margin-bottom: 1.5rem;
  `,
  imgGrid: css`
    display: flex;
    justify-content: right;
    align-items: center;
  `,
  iconSpacing: (theme: Theme) => css`
    margin-left: ${theme.spacing(8)};
  `,
  background: (theme: Theme) => css`
    background-color: #ebf3ff;
    border-radius: ${theme.spacing(8)};
    ${theme.breakpoints.down('xs')} {
      border-radius: 0;
    }
  `,
};

const NudgeBanner: React.FunctionComponent<{ academicEvent: AcademicEvent }> = ({ academicEvent }) => {
  return (
    <TrackedDiv
      trackingName="nudge_banner"
      trackClicks={false}
      requireFullyVisible={false}
      withVisibilityTracking={true}
    >
      <Grid aria-label={_t('Nudge Banner')} css={styles.background}>
        <Grid container alignContent="center" css={styles.contentContainer}>
          <Grid item xs={12}>
            <Hidden smUp>
              <img src={calendarImage} alt={_t('Calendar icon')} width="74" css={styles.bottomSpace} />
            </Hidden>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography variant="h1" color="body" css={styles.bannerHeading}>
              {academicEvent.eventTitle}
            </Typography>
            <Typography2 component="p" css={styles.bannerDescription} variant="bodyPrimary">
              {academicEvent.description}
            </Typography2>
            {!!academicEvent.actionLink && (
              <Button
                variant="primary"
                size="small"
                component={TrackedLink2}
                href={academicEvent.actionLink}
                withVisibilityTracking
                aria-label={_t('Enroll Now')}
                trackingName="nudge_banner_cta"
                target="_blank"
                rel="noopener noreferrer"
              >
                {_t('Enroll Now')}
                <ExternalLinkIcon css={styles.iconSpacing} />
              </Button>
            )}
          </Grid>
          <Grid item xs={12} sm={3} css={styles.imgGrid}>
            <Hidden xsDown>
              <img src={calendarImage} alt={_t('calendar icon')} width="192" />
            </Hidden>
          </Grid>
        </Grid>
      </Grid>
    </TrackedDiv>
  );
};

export default NudgeBanner;
